import React, { useMemo, useCallback } from "react";
import { getVal, setVal, TabBarV2 } from "oolib";

import { useSankeyConfig } from "./ConfigForm_Context";
import { ConfigForm_SectionWrapper } from "../../WidgetMetricCard/ConfigForm/ConfigForm_SectionWrapper";
import { ReportsLightboxInput } from "../../WidgetBarChart/WIDGET_ELEM_ConfigForm";
import { getPickerProp } from "../../utils/pickerVariantConfig";
import { useGetAllOptionsForDimension1 } from "../../WidgetBarChart/utils/useGetAllOptionsForDimension1";
import { useGetAllDateFieldOptions } from "../../WidgetLineChart/utils";
import { DATA_KEYS } from "../DATA_KEYS";

const CATEGORICAL_COMPS = [
  "RadioList", "DropdownSingle", "CheckboxList",
  "DropdownMulti", "TagsInputSingle", "TagsInputMulti",
];

const ORDER_BY_OPTIONS = [
  { display: "Order Index", value: "orderIdx" },
  { display: "Timestamp", value: "timestamp" },
];

/**
 * Filter grouped options array to only include options matching
 * specific component types, and optionally a specific contentType.
 */
const filterGroupedOptions = (groupedOptions, { compTypes, contentType }) => {
  return groupedOptions
    .map((group) => ({
      ...group,
      options: (group.options || []).filter((opt) => {
        if (compTypes && !compTypes.includes(opt.comp)) return false;
        if (contentType && opt.contentType !== contentType) return false;
        return true;
      }),
    }))
    .filter((group) => group.options.length > 0);
};


export const ConfigForm_EventFlowSection = () => {
  const { updateWidget, widgetConfig } = useSankeyConfig();

  const eventFlowConfig = getVal(widgetConfig, DATA_KEYS.eventFlowConfig) || {};
  const eventNameField = eventFlowConfig.eventNameField;
  const idField = eventFlowConfig.idField;
  const orderByMode = eventFlowConfig.orderByMode || "orderIdx";
  const orderField = eventFlowConfig.orderField;
  const timeField = eventFlowConfig.timeField;

  const selectedContentType = eventNameField?.contentType;

  // All categorical field options (grouped by contentType)
  const { options: allDimOptions } = useGetAllOptionsForDimension1({
    showNumberInputsInOptions: true,
  });

  // All date field options (grouped by contentType)
  const { options: allDateOptions } = useGetAllDateFieldOptions();

  // --- Filtered option lists ---

  // Event name: all categorical fields across all content types
  const eventNameOptions = useMemo(
    () => filterGroupedOptions(allDimOptions, { compTypes: CATEGORICAL_COMPS }),
    [allDimOptions]
  );

  // ID field: categorical fields from the selected contentType
  const idFieldOptions = useMemo(() => {
    if (!selectedContentType) return [];
    return filterGroupedOptions(allDimOptions, {
      compTypes: CATEGORICAL_COMPS,
      contentType: selectedContentType,
    });
  }, [allDimOptions, selectedContentType]);

  // Order index field: NumberInput fields from the selected contentType
  const orderFieldOptions = useMemo(() => {
    if (!selectedContentType) return [];
    return filterGroupedOptions(allDimOptions, {
      compTypes: ["NumberInput"],
      contentType: selectedContentType,
    });
  }, [allDimOptions, selectedContentType]);

  // Time field: date fields from the selected contentType
  const timeFieldOptions = useMemo(() => {
    if (!selectedContentType) return [];
    return filterGroupedOptions(allDateOptions, {
      contentType: selectedContentType,
    });
  }, [allDateOptions, selectedContentType]);

  // --- Handlers ---

  const handleEventNameChange = useCallback(
    (_, value) => {
      updateWidget((w) => {
        // Clear dependent fields when event name (and thus contentType) changes
        w = setVal(w, DATA_KEYS.eventFlowConfig, {
          eventNameField: value,
          orderByMode: getVal(w, DATA_KEYS.efOrderByMode) || "orderIdx",
        });
        // Auto-title
        const hasCustomTitle = getVal(w, DATA_KEYS.hasCustomTitle);
        if (!hasCustomTitle && value?.display) {
          w = setVal(w, DATA_KEYS.title, `Event Flow: ${value.display}`);
        }
        return w;
      });
    },
    [updateWidget]
  );

  const handleIdFieldChange = useCallback(
    (_, value) => {
      updateWidget((w) => {
        w = setVal(w, DATA_KEYS.efIdField, value);
        return w;
      });
    },
    [updateWidget]
  );

  const handleOrderByModeChange = useCallback(
    (_, value) => {
      updateWidget((w) => {
        w = setVal(w, DATA_KEYS.efOrderByMode, value);
        // Clear the order/time field when switching mode
        w = setVal(w, DATA_KEYS.efOrderField, undefined);
        w = setVal(w, DATA_KEYS.efTimeField, undefined);
        return w;
      });
    },
    [updateWidget]
  );

  const handleOrderFieldChange = useCallback(
    (_, value) => {
      updateWidget((w) => {
        w = setVal(w, DATA_KEYS.efOrderField, value);
        return w;
      });
    },
    [updateWidget]
  );

  const handleTimeFieldChange = useCallback(
    (_, value) => {
      updateWidget((w) => {
        w = setVal(w, DATA_KEYS.efTimeField, value);
        return w;
      });
    },
    [updateWidget]
  );

  return (
    <ConfigForm_SectionWrapper icon="FlowArrow" title="Event Flow Config">
      <div style={{ display: "flex", flexDirection: "column", gap: "0.75rem" }}>
        <ReportsLightboxInput
          id="ef-event-name"
          label="Event Name Field"
          placeholder="Select event name field"
          options={eventNameOptions}
          value={eventNameField}
          onChange={handleEventNameChange}
          picker={getPickerProp({ widgetComp: 'WidgetSankeyChart', slot: 'eventFlow' })}
        />

        <ReportsLightboxInput
          id="ef-id-field"
          label="Entity ID Field"
          placeholder="Select entity identifier"
          disabled={!selectedContentType}
          options={idFieldOptions}
          value={idField}
          onChange={handleIdFieldChange}
          picker={getPickerProp({ widgetComp: 'WidgetSankeyChart', slot: 'eventFlow' })}
        />

        <TabBarV2
          options={ORDER_BY_OPTIONS}
          value={orderByMode}
          onChange={handleOrderByModeChange}
          saveValueAsString
          S
        />

        {orderByMode === "orderIdx" ? (
          <ReportsLightboxInput
            id="ef-order-field"
            label="Order Index Field"
            placeholder="Select numeric ordering field"
            disabled={!selectedContentType}
            options={orderFieldOptions}
            value={orderField}
            onChange={handleOrderFieldChange}
            picker={getPickerProp({ widgetComp: 'WidgetSankeyChart', slot: 'eventFlow' })}
          />
        ) : (
          <ReportsLightboxInput
            id="ef-time-field"
            label="Timestamp Field"
            placeholder="Select date/time field"
            disabled={!selectedContentType}
            options={timeFieldOptions}
            value={timeField}
            onChange={handleTimeFieldChange}
            picker={getPickerProp({ widgetComp: 'WidgetSankeyChart', slot: 'eventFlow' })}
          />
        )}
      </div>
    </ConfigForm_SectionWrapper>
  );
};
